//#region IMPORTS
import * as XLSX from "xlsx";
import { InstoreOrder, InstoreOrderLine } from "../models/instoreOrder";
import normalizeCustomFields from "./normalizeCustomFields";
import { autoFitColumns } from "./autoFitColumns";
//#endregion

// Excel export of perso lines (instore order lines with custom_fields), built
// client-side with SheetJS — the backend Excel export (InstoreOrderLinesExport)
// carries no perso at all. One row per perso line: the fixed columns below,
// then one column per perso field name (union over all rows), so every line
// has its value in the right column. Used by the "n perso" pill in the list
// (one order) and by the perso-check modal (every checked order with perso).

export type PersoExportRow = Record<string, string | number>;

// Same naming style as the backend line export, so pickers see familiar
// headers.
export const PERSO_FIXED_COLUMNS = [
  "ORDER_CODE",
  "STORE_ID",
  "STORE_TYPE",
  "STORE_FORMAT",
  "STORE_LANGUAGE",
  "PRODUCT",
  "AANTAL",
] as const;

// Rows for the perso lines of one order (detail payload — the list has no
// lines). Orders without perso lines yield [].
export function buildPersoRows(order: InstoreOrder): PersoExportRow[] {
  const lines: InstoreOrderLine[] = order.instore_order_lines ?? [];
  return lines.flatMap((line) => {
    const fields = normalizeCustomFields(line.custom_fields);
    if (fields.length === 0) return [];
    const row: PersoExportRow = {
      ORDER_CODE: order.order_code,
      STORE_ID: order.external_store_id,
      STORE_TYPE: order.instore_type,
      STORE_FORMAT: line.store_format_name,
      STORE_LANGUAGE: order.store_language,
      PRODUCT: line.article_name,
      AANTAL: line.amount,
    };
    for (const field of fields) row[field.fieldName] = field.fieldValue ?? "";
    return [row];
  });
}

// Writes the rows to a one-sheet workbook and triggers the download. Field
// columns are the union of the field names, in first-seen order, after the
// fixed columns; missing cells are "" so the sheet is rectangular.
export function exportPersoRowsToExcel(
  rows: PersoExportRow[],
  fileName: string,
): void {
  const fieldColumns: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (
        !(PERSO_FIXED_COLUMNS as readonly string[]).includes(key) &&
        !fieldColumns.includes(key)
      )
        fieldColumns.push(key);
    }
  }
  const header = [...PERSO_FIXED_COLUMNS, ...fieldColumns];
  const rectangular = rows.map((row) =>
    Object.fromEntries(header.map((key) => [key, row[key] ?? ""])),
  );

  const worksheet = XLSX.utils.json_to_sheet(rectangular, { header });
  autoFitColumns(worksheet, rectangular);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "perso");
  XLSX.writeFile(workbook, fileName);
}
